import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import { Progress } from '@components/atoms/Progress';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { formatCurrency } from '@utils/formatCurrency';
import { calculateBudgetProgress } from '@utils/budgetProgress';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetCardProps {
  totalCost: number;
  budget: number;
  setIsBudgetModalOpen: () => void;
  setBillOfMaterialsModalOpen?: () => void;
}

export const BudgetCard = ({
  totalCost,
  budget,
  setIsBudgetModalOpen,
  setBillOfMaterialsModalOpen,
}: BudgetCardProps) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.budgetCard;
  const budgetProgress = calculateBudgetProgress(totalCost, budget);
  const { percentage, status, displayText } = budgetProgress;
  return (
    <Card className="flex flex-col min-h-[13rem] p-4 w-full rounded-tr-none rounded-br-none">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <PaymentsOutlinedIcon
            sx={{ fontSize: '24px' }}
            className="text-orange-400"
          />
          <h1 className="text-base font-semibold">{t.title}</h1>
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col justify-between">
            <h1 className="text-sm font-medium">{t.totalCost}</h1>
            <h1 className="text-sm font-medium">{formatCurrency(totalCost)}</h1>
          </div>
          <div className="flex flex-col justify-between">
            <h1 className="text-sm font-medium">{t.maxBudget}</h1>
            <h1 className="text-sm font-medium">{formatCurrency(budget)}</h1>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
            <span>{t.progress}</span>
            <span>{displayText}</span>
          </div>
          <Progress
            size="sm"
            variant={status}
            value={percentage}
            showPercentage={false}
          />
        </div>
      </div>

      {/* Actions pinned to the bottom of the card (icon + text) */}
      <div className="mt-auto flex items-center gap-2 pt-3">
        {setBillOfMaterialsModalOpen && (
          <Button
            variant="tertiary"
            onClick={setBillOfMaterialsModalOpen}
            leftIcon={<InventoryOutlinedIcon sx={{ fontSize: 18 }} />}
            className="flex-1 !justify-center whitespace-nowrap">
            {t.materials}
          </Button>
        )}
        <Button
          variant="tertiary"
          onClick={setIsBudgetModalOpen}
          leftIcon={<RemoveRedEyeOutlinedIcon sx={{ fontSize: 18 }} />}
          className="flex-1 !justify-center whitespace-nowrap">
          {t.details}
        </Button>
      </div>
    </Card>
  );
};
